'use client';
import React from 'react';
import { useCart } from '@shopify/hydrogen-react';
import { Cart as SharedCart } from '@repo/ui/src/components/Cart';
import type { CartTotals } from '@repo/ui/src/types/cart';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { mapHydrogenCartLineToCartLineItem } from '@src/domain/mappers/shopifyCartMapper';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import { useUIListener } from '@repo/ui/src/modules/UI/hooks/useUIListener';
import { cartViewed, checkoutStarted } from '@repo/ui/src/services/analytics';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';

interface RefersionGlobal {
  sendCheckoutEvent: (...args: Array<string | null>) => Promise<void>;
}

const MeaterCart = () => {
  const { id, lines, cost, totalQuantity, checkoutUrl, linesUpdate, linesRemove, status } =
    useCart();
  const { t } = useTranslation();
  const { locale } = useCurrentUrl();
  const { close } = useModal(ModalID.cart);

  const isLoading = status === 'fetching' || status === 'creating' || status === 'updating';

  const lineItems = React.useMemo(
    () => (lines ?? []).map(mapHydrogenCartLineToCartLineItem),
    [lines],
  );

  useUIListener(
    ModalID.cart,
    React.useCallback(
      (isOpen: boolean) => {
        if (!isOpen) return;
        cartViewed({
          action: 'open cart',
          basket_token: id?.split('/').pop() ?? '',
          products: lineItems.map((item, index) => ({
            category: '',
            description: '',
            image_url: item.imageUrl,
            name: item.title,
            position: index,
            price: item.price,
            product_id: (item.meta?.handle as string) ?? item.id,
            quantity: item.quantity,
            sku: (item.meta?.handle as string) ?? item.id,
            url: '',
          })),
        });
      },
      [id, lineItems],
    ),
  );

  const computedTotalItems = (lines ?? []).reduce((sum, line) => sum + (line.quantity ?? 0), 0);

  const totals: CartTotals = {
    subtotal: parseFloat(cost?.subtotalAmount?.amount ?? '0'),
    shippingTotal: 0,
    tax: parseFloat(cost?.totalTaxAmount?.amount ?? '0'),
    total: parseFloat(cost?.totalAmount?.amount ?? '0'),
    totalItems: totalQuantity ?? computedTotalItems,
    currency: cost?.totalAmount?.currencyCode ?? 'USD',
  };

  const handleQuantityChange = React.useCallback(
    (id: string, quantity: number) => {
      linesUpdate([{ id, quantity }]);
    },
    [linesUpdate],
  );

  const handleRemove = React.useCallback(
    (id: string) => {
      linesRemove([id]);
    },
    [linesRemove],
  );

  const handleCheckout = React.useCallback(async () => {
    if (checkoutUrl) {
      const cartToken = id?.split('/').pop();

      checkoutStarted({
        affiliation: 'website',
        basket_token: cartToken ?? '',
        brand: 'MEATER',
        currency: totals.currency,
        products: lineItems.map((item, index) => ({
          category: '',
          description: '',
          image_url: item.imageUrl,
          name: item.title,
          position: index,
          price: item.price,
          product_id: (item.meta?.handle as string) ?? item.id,
          quantity: item.quantity,
          sku: (item.meta?.handle as string) ?? item.id,
          url: '',
        })),
      });

      const refersion = (window as Window & { r?: RefersionGlobal }).r;
      try {
        if (cartToken && refersion?.sendCheckoutEvent) {
          await Promise.race([
            refersion.sendCheckoutEvent(
              cartToken,
              localStorage.getItem('rfsn_v4_id'),
              window.location.href,
              localStorage.getItem('rfsn_v4_aid'),
              localStorage.getItem('rfsn_v4_cs'),
            ),
            new Promise<void>((_, reject) => setTimeout(() => reject(), 500)),
          ]);
        }
      } catch {
        // intentionally swallowed — Refersion must never block checkout
      }
      window.location.href = checkoutUrl;
    }
  }, [checkoutUrl, id, lineItems, totals.currency]);

  const emptyCtaNode = (
    <Button
      variant={ButtonVariant.secondary}
      className="w-full text-center rounded"
      onClick={close}
      data-testid="cart-footer-continue-shopping"
    >
      {t('cartContent.continueShopping', undefined, { default: 'Continue Shopping' })}
    </Button>
  );

  return (
    <SharedCart
      isLoading={isLoading}
      lineItems={lineItems}
      totals={totals}
      onQuantityChange={handleQuantityChange}
      onRemove={handleRemove}
      onCheckout={handleCheckout}
      title={t('cartContent.cartHeader', undefined, { default: 'Your Cart' })}
      emptyMessage={t('cartContent.emptyMessage', undefined, {
        default: 'Your cart is currently empty.',
      })}
      estimatedTotalLabel={t('cartContent.estimatedTotal', undefined, {
        default: 'Estimated Total',
      })}
      checkoutLabel={t('cartContent.checkoutButton', undefined, {
        default: 'Continue to Checkout',
      })}
      removeLabel={t('cartContent.removeText', undefined, { default: 'Remove' })}
      subtotalLabel={t(
        'orderSummary.subtotalItems',
        { totalItems: totals.totalItems },
        { default: 'Subtotal ({{totalItems}} items)' },
      )}
      shippingLabel={t('cart.shipping', undefined, { default: 'Shipping' })}
      shippingFreeLabel={t('addToCartWindowContent.shippingFreeLabel', undefined, {
        default: 'Free',
      })}
      taxLabel={t('cart.estimatedTax', undefined, { default: 'Estimated Tax' })}
      taxFreeLabel={t('addToCartWindowContent.taxFreeLabel', undefined, { default: '-' })}
      outOfStockMessage={t('cart.outOfStockText', undefined, {
        default: 'This item is out of stock. Remove from your cart to complete your purchase.',
      })}
      outOfStockLabel={t('productDetailPageContent.outOfStockText', undefined, {
        default: 'Out of Stock',
      })}
      quantityLabel={t('cart.quantityText', undefined, { default: 'Qty' })}
      locale={locale}
      emptyCtaNode={emptyCtaNode}
    />
  );
};

export default MeaterCart;
